import React from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, ChevronRight, CreditCard, Loader2, X } from "lucide-react";
import type { EstadoDeCobro } from "../../lib/estadoDeCobro";
import { abrirCambioDeTarjeta, abrirCambioDeTarjetaDeMarca } from "../../lib/cobroRechazado";
import { cuerpoDeCobro, tituloDeCobro } from "../../lib/textosDeCobro";

/**
 * Aviso de cobro rechazado (ADR-2026-10-26-cobros-rechazados-en-la-plataforma). Recibe el estado
 * ya decidido por `estadoDeCobro` y lo dibuja igual en todos lados; los textos salen de
 * `lib/textosDeCobro`.
 *
 *  - `cartel`: la tarjeta con acciones (home y /subscription).
 *  - `modal`: el mismo contenido sin la caja, para adentro de `ModalDeCobro` (sidebar).
 *  - `compacto`: la cajita del header mobile; toda la caja lleva a /subscription.
 *
 * Con `estado.marca` (facturación por marca, ADR-2026-10-29-cobro-rechazado-por-marca) el aviso es
 * el del plan de esa marca: "Cambiar tarjeta y pagar" va a `/marca/cambiar-tarjeta`, no hay "Pagar
 * ahora" (lo cobra Rebill al cambiar la tarjeta) y lo que lleva a /subscription lleva a
 * Configuración → Suscripción, donde está cada marca. Un miembro no ve acciones: el texto le dice que
 * se lo pida al admin. Sin `marca`, todo como siempre.
 */

const CLAVE_CERRADO = (clave: string) => `aviso-de-cobro-cerrado:${clave}`;

function leerCerrado(estado: EstadoDeCobro): boolean {
  if (estado.tipo !== "dado_de_baja") return false;
  try {
    return window.localStorage.getItem(CLAVE_CERRADO(estado.claveCierre)) === "1";
  } catch {
    return false;
  }
}

export function tonoDeCobro(estado: EstadoDeCobro): { caja: string; icono: string } {
  if (estado.tipo === "dado_de_baja") return { caja: "bg-white border-neutral-200", icono: "text-brand-black/50" };
  if (estado.tipo === "baja_programada") return { caja: "bg-red-50 border-red-200", icono: "text-red-600" };
  return { caja: "bg-amber-50 border-amber-200", icono: "text-amber-600" };
}

export const AvisoDeCobro: React.FC<{
  estado: EstadoDeCobro;
  className?: string;
  variante?: "cartel" | "modal" | "compacto";
  /** Estamos en /subscription: no se ofrece "ir a /subscription", el catálogo está abajo. */
  enSuscripcion?: boolean;
  /** "Pagar ahora" (Rebill, estado B). Sin esto el botón lleva a /subscription, que lo tiene. */
  onPagarAhora?: () => void;
  pagando?: boolean;
  /** Un aviso del que lo monta (ej. "el pago volvió a rechazarse"), debajo de las acciones. */
  aviso?: string | null;
  /** Antes de navegar a /subscription (el header mobile cierra su menú, el modal se cierra). */
  onNavegar?: () => void;
}> = ({ estado, className, variante = "cartel", enSuscripcion, onPagarAhora, pagando, aviso, onNavegar }) => {
  const navigate = useNavigate();
  const [cerrado, setCerrado] = React.useState(() => variante === "cartel" && leerCerrado(estado));
  const [saliendo, setSaliendo] = React.useState(false);
  const [abriendoTarjeta, setAbriendoTarjeta] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  // TEMPORAL (lib/simulacionDeCobro): en un aviso simulado los botones sólo cuentan qué harían.
  const [notaSimulacion, setNotaSimulacion] = React.useState<string | null>(null);

  if (cerrado) return null;

  const urgente = estado.tipo === "baja_programada";
  const tono = tonoDeCobro(estado);
  const marca = estado.tipo !== "dado_de_baja" ? estado.marca : undefined;
  const irASuscripcion = () => {
    onNavegar?.();
    if (marca) navigate("/configuracion", { state: { section: "plan" } });
    else navigate("/subscription");
  };

  if (variante === "compacto") {
    // Un miembro no entra a Suscripción (es del dueño): la caja sólo avisa.
    if (marca && !marca.esDueno) {
      return (
        <div className={`flex w-full items-start gap-2 rounded-md border px-3 py-2.5 ${tono.caja} ${className ?? ""}`}>
          <AlertCircle className={`mt-0.5 h-4 w-4 shrink-0 ${tono.icono}`} />
          <span>
            <span className="block text-[13px] font-medium text-brand-black/90">
              {tituloDeCobro(estado)}
              {estado.simulado ? " (simulación)" : ""}
            </span>
            <span className="mt-0.5 block text-[11px] leading-snug text-brand-black/60">
              Pedile al admin de la cuenta que cambie la tarjeta.
            </span>
          </span>
        </div>
      );
    }
    return (
      <button
        type="button"
        onClick={irASuscripcion}
        className={`flex w-full items-start gap-2 rounded-md border px-3 py-2.5 text-left transition-opacity active:opacity-80 ${tono.caja} ${className ?? ""}`}
      >
        <AlertCircle className={`mt-0.5 h-4 w-4 shrink-0 ${tono.icono}`} />
        <span>
          <span className="block text-[13px] font-medium text-brand-black/90">
            {tituloDeCobro(estado)}
            {estado.simulado ? " (simulación)" : ""}
          </span>
          <span className="mt-0.5 block text-[11px] leading-snug text-brand-black/60">
            {estado.tipo === "dado_de_baja" ? "Podés volver a suscribirte cuando quieras." : "Tocá para ver los detalles."}
          </span>
        </span>
      </button>
    );
  }

  const cambiarTarjeta = async () => {
    if (estado.simulado) {
      setNotaSimulacion(
        marca
          ? `Simulación: acá se abriría la página de Rebill para cambiar la tarjeta de ${marca.nombre}, que cobra el plan en el momento.`
          : estado.plataforma === "mp"
            ? "Simulación: acá se abriría tu cuenta de MercadoPago en otra pestaña."
            : estado.plataforma === "stripe"
              ? "Simulación: acá se abriría el portal de facturación de Stripe."
              : "Simulación: acá se abriría la página de Rebill para cambiar la tarjeta, que cobra el plan en el momento.",
      );
      return;
    }
    if (abriendoTarjeta) return;
    setError(null);
    setAbriendoTarjeta(true);
    try {
      if (marca) {
        await abrirCambioDeTarjetaDeMarca(marca.brandId);
        return;
      }
      await abrirCambioDeTarjeta(estado.plataforma);
      // MercadoPago abre otra pestaña: acá no se navega, el botón vuelve a estar disponible.
      if (estado.plataforma === "mp") setAbriendoTarjeta(false);
    } catch (e) {
      setAbriendoTarjeta(false);
      setError(e instanceof Error ? e.message : "No pudimos abrir el cambio de tarjeta. Probá de nuevo.");
    }
  };

  const cerrar = () => {
    if (estado.tipo !== "dado_de_baja") return;
    try {
      window.localStorage.setItem(CLAVE_CERRADO(estado.claveCierre), "1");
    } catch {
      /* sin storage se cierra sólo por esta vez */
    }
    setSaliendo(true);
    window.setTimeout(() => setCerrado(true), 150);
  };

  const pagarAhora =
    urgente && estado.plataforma === "rebill" && !marca ? (
      <button
        type="button"
        disabled={pagando}
        onClick={() =>
          estado.simulado
            ? setNotaSimulacion("Simulación: acá se cobraría el plan con la tarjeta guardada.")
            : onPagarAhora
              ? onPagarAhora()
              : irASuscripcion()
        }
        className="inline-flex items-center justify-center gap-1.5 rounded-md bg-brand-primary px-4 py-2 text-[13.5px] font-medium text-white shadow-sm transition-[background-color,transform] duration-150 ease-out hover:bg-brand-primary-hover enabled:active:scale-[0.96] disabled:opacity-60 sm:text-[14px]"
      >
        {pagando ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        {pagando ? "Cobrando…" : "Pagar ahora"}
      </button>
    ) : null;

  const botonTarjeta =
    estado.tipo !== "dado_de_baja" && estado.plataforma && (!marca || marca.esDueno) ? (
      <button
        type="button"
        disabled={abriendoTarjeta}
        onClick={cambiarTarjeta}
        className="inline-flex items-center justify-center gap-1.5 rounded-md border border-neutral-200 bg-white px-4 py-2 text-[13.5px] font-medium text-brand-black/90 shadow-sm transition-[background-color,transform] duration-150 ease-out hover:bg-neutral-50 enabled:active:scale-[0.96] disabled:opacity-60 sm:text-[14px]"
      >
        {abriendoTarjeta ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
        {/* Rebill cobra el pendiente apenas se cambia la tarjeta (en `retrying` y en la morosa,
            verificado en payment_events el 2026-10-27): el botón lo dice. */}
        {estado.plataforma === "mp"
          ? "Cambiar tarjeta en MercadoPago"
          : estado.plataforma === "rebill"
            ? "Cambiar tarjeta y pagar"
            : "Cambiar tarjeta"}
      </button>
    ) : null;

  // Con varias marcas rechazadas el botón de arriba es el de la más urgente; las demás, en Suscripción.
  const verMarcas =
    marca?.esDueno && marca.otras.length > 0 ? (
      <button
        type="button"
        onClick={irASuscripcion}
        className="inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-2 text-[13.5px] font-medium text-brand-primary transition-[color,transform] duration-150 ease-out hover:text-brand-primary-hover active:scale-[0.96] sm:text-[14px]"
      >
        Ver todas las marcas
        <ChevronRight className="h-4 w-4" />
      </button>
    ) : null;

  const volver =
    estado.tipo === "dado_de_baja" && !enSuscripcion ? (
      <button
        type="button"
        onClick={irASuscripcion}
        className="inline-flex items-center justify-center gap-1.5 rounded-md bg-brand-primary px-4 py-2 text-[13.5px] font-medium text-white shadow-sm transition-[background-color,transform] duration-150 ease-out hover:bg-brand-primary-hover active:scale-[0.96] sm:text-[14px]"
      >
        Volver a suscribirme
        <ChevronRight className="h-4 w-4" />
      </button>
    ) : null;

  const contenido = (
    <>
      <div className={`flex items-center gap-2 ${variante === "cartel" ? "pr-6" : "pr-8"}`}>
        <AlertCircle className={`h-4 w-4 shrink-0 ${tono.icono}`} />
        <h3 className="text-[14px] font-semibold text-brand-black/90 sm:text-[15px]">{tituloDeCobro(estado)}</h3>
        {estado.simulado && (
          <span className="rounded-sm bg-neutral-900/80 px-1.5 py-0.5 text-[11px] font-medium text-white">
            Simulación
          </span>
        )}
      </div>
      <p className="mt-1 text-[13px] leading-snug text-brand-black/60 sm:text-[14px] sm:leading-normal">
        {cuerpoDeCobro(estado)}
      </p>
      {(pagarAhora || botonTarjeta || verMarcas || volver) && (
        <div className="mt-3 flex flex-wrap gap-2">
          {pagarAhora}
          {botonTarjeta}
          {verMarcas}
          {volver}
        </div>
      )}
      {(error || aviso) && <p className="mt-2 text-[12.5px] text-red-700">{error ?? aviso}</p>}
      {notaSimulacion && <p className="mt-2 text-[12.5px] text-brand-black/60">{notaSimulacion}</p>}
    </>
  );

  if (variante === "modal") return <div className={className}>{contenido}</div>;

  return (
    <div className={className}>
      <div
        role={urgente ? "alert" : "status"}
        className={`relative rounded-lg border p-4 transition-[opacity,transform,filter] ease-out ${tono.caja} ${
          saliendo
            ? "opacity-0 duration-150 motion-safe:-translate-y-3 motion-safe:blur-[4px]"
            : "opacity-100 duration-300"
        }`}
      >
        {estado.tipo === "dado_de_baja" && (
          <button
            type="button"
            onClick={cerrar}
            aria-label="Cerrar aviso"
            className="absolute right-2 top-2 rounded-sm p-1.5 text-brand-black/40 transition-colors hover:text-brand-black/70"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        {contenido}
      </div>
    </div>
  );
};
